import React, { useContext } from 'react';
import { ToastContainer } from 'react-toastify';
import Sidebar from '../shared/Sidebar/Sidebar';
import DfcVillageMenuDrawer from '../shared/dfcVillage/MenuDrawer';
import DfcAuthOverlayProvider from '../shared/dfcVillage/DfcAuthOverlay';
import Header from '../shared/header';
import MobileBottomNavigation from '../shared/MobileBottomNavigation';
import { ILayoutProps } from './Types';
import { useLocation } from 'react-router-dom';
import { Stack, useMediaQuery } from '@mui/material';
import { CheckoutContext, CheckoutContextProps } from '../../context/CheckoutContext';
import { WAKEUP_WATERSPORTS_COMPANY_ID } from '../../Utils/constants';
import { useDfcPageDirection } from '../../theme/dfcDirection';
import { isAsaCompany } from '../../Utils/asaBrand';

/**
 * Village flow pages with a `dfcVillageTheme` layout of their own (see
 * `components/shared/dfcVillage/flow`). Lower-case: React Router matches paths case-insensitively.
 */
const DFC_VILLAGE_FLOW_PATHS = [
  '/cart',
  '/cartcheckout',
  '/addons',
  '/confirmation',
  '/cancellation',
  '/my-profile',
];

/** All Sports Arena's own full-width pages (vouchers, memberships, their purchase and confirmation). */
const ASA_FULL_WIDTH_PATHS = [
  '/packages',
  '/memberships',
  '/packagepurchase',
  '/membershippurchase',
  '/packageconfirmation',
];

const Layout: React.FC<ILayoutProps> = ({ isOpen, toggleDrawer, children }) => {
  const { pathname } = useLocation();
  const isMd = useMediaQuery('(min-width:768px)');

  const { company } = useContext(CheckoutContext) as CheckoutContextProps;
  const { direction } = useDfcPageDirection();

  // The Fitness Village theme brings its own mobile menu, styled for its light / dark surface,
  // and always offers it — the canvas draws the burger on every village screen.
  const isDfcVillage = company?.theme === 'dfcVillageTheme';

  // Full width pages. The village booking flow draws its own page background and column.
  const isFullWidthPage =
    pathname === '/' ||
    pathname === '/class-session' ||
    pathname === '/FitnessVillage' ||
    (isDfcVillage && DFC_VILLAGE_FLOW_PATHS.includes(pathname.toLowerCase())) ||
    (isAsaCompany(company) && ASA_FULL_WIDTH_PATHS.includes(pathname.toLowerCase()));

  const showHeader = () => {
    if (pathname === '/') {
      // The Wakeup home ships its own nav; the app header would duplicate it
      // on mobile.
      if (company?.id === WAKEUP_WATERSPORTS_COMPANY_ID && !isMd) {
        return false;
      }
      return !company?.hasMobileNavigation || isMd;
    } else {
      return true;
    }
  };

  const MenuDrawer = isDfcVillage ? DfcVillageMenuDrawer : Sidebar;
  const showMenuDrawer = isDfcVillage || company?.showSidebar;

  return (
    // Inert unless a Fitness Village control opens it — see DfcAuthOverlayProvider.
    <DfcAuthOverlayProvider>
      <Stack
        sx={{
          width: isFullWidthPage ? '100%' : '97%',
          marginX: isFullWidthPage ? 0 : 'auto',
          marginBottom: isFullWidthPage ? 0 : pathname !== '/' ? '20px' : 0,
          justifyContent: 'flex-start',
          alignItems: 'center',
          minHeight: 'calc(100vh - 40px)',
          paddingBottom: company?.hasMobileNavigation ? { xs: '80px', md: 0 } : 0,

          '@media (min-width: 768px)': {
            width: isFullWidthPage ? '100%' : '80%',
            maxWidth: isFullWidthPage ? '100%' : '768px',
          },
        }}
      >
        <ToastContainer hideProgressBar rtl={direction === 'rtl'} />
        {showMenuDrawer && <MenuDrawer isOpen={isOpen} toggleDrawer={toggleDrawer} />}
        {showHeader() && (
          <Header company={company} isSidebarOpen={isOpen} toggleDrawer={toggleDrawer} />
        )}
        {children}
      </Stack>
      {company?.hasMobileNavigation && <MobileBottomNavigation />}
    </DfcAuthOverlayProvider>
  );
};

export default Layout;
